import { ASSET_CLASS_IDS, MEASURE_SLUGS } from "~/consts";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { Filter, FilterOperators } from "~/modules/filters/types";
import { ColumnSpanConfig } from "~/types";
import { getAllTokensStackedChart } from "~/modules/token_timeseries/shared/getAllTokensStackedChart";
import { VisualizationModeCard } from "~/modules/visualization/components/Timeseries/VisualizationModeCard";
import { VisualizationMode } from "~/modules/visualization/components/Timeseries/types";
import { getHoldersChart } from "~/modules/token_timeseries/shared/getHoldersChart";
import { getMintBurnVolumeChart } from "~/modules/token_timeseries/shared/getMintBurnVolumeChart";
import { getActiveAddressesChart } from "~/modules/token_timeseries/shared/getActiveAddressesChart";
import { getTransferVolumeChart } from "~/modules/token_timeseries/shared/getTransferVolumeChart";
import { Network } from "~/modules/networks/types";

// // // //

/**
 * CantonNetworkTimeSeriesCard
 * Encapsulates markup for the Network TimeSeries card and logic for toggling between charts
 */
export function CantonNetworkTimeSeriesCard(props: {
    title?: string;
    filters: Filter[];
    defaultGroupBy: TokenTimeseriesGroupBy;
    enableGroupByControls: boolean;
    columnSpanConfig?: ColumnSpanConfig;
}) {
    const {
        title = "Network Metrics",
        filters,
        defaultGroupBy,
        enableGroupByControls,
        columnSpanConfig = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 12,
            xxl: 12,
        },
    } = props;

    // Define VisualizationModes
    const MARKET_CAP = getAllTokensStackedChart({
        key: "network-all-stacked",
        label: "Tokenized Asset Value",
        description: "",
        filters: [
            ...filters,
            {
                field: TokenTimeseriesColumns.assetClassID,
                operator: FilterOperators.NotEquals,
                value: ASSET_CLASS_IDS.Stablecoin,
            },
        ],
        defaultGroupBy,
        enableGroupByControls,
        measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
        loginRequired: false,
        ignoreDownloadsFeatureFlag: true,
        groupByOptions: [
            TokenTimeseriesGroupBy.asset,
            TokenTimeseriesGroupBy.assetClass,
            TokenTimeseriesGroupBy.network,
            TokenTimeseriesGroupBy.token,
            TokenTimeseriesGroupBy.peggedAsset,
            TokenTimeseriesGroupBy.protocol,
            TokenTimeseriesGroupBy.issuer,
            TokenTimeseriesGroupBy.jurisdictionCountry,
        ],
    });

    const modes = [MARKET_CAP];

    return (
        <VisualizationModeCard
            title={title}
            modes={modes}
            columnSpanConfig={columnSpanConfig}
        />
    );
}

// // // //

export function buildNetworkTimeSeriesModes(props: {
    network: Network;
    filters: Filter[];
    defaultGroupBy: TokenTimeseriesGroupBy;
    enableGroupByControls: boolean;
}): VisualizationMode[] {
    const { network, filters, defaultGroupBy, enableGroupByControls } = props;

    // Define key prefix for chart filenames
    const prefixKey = network.slug;

    // Define shared group-by options
    const groupByOptions = [
        TokenTimeseriesGroupBy.asset,
        TokenTimeseriesGroupBy.assetClass,
        TokenTimeseriesGroupBy.protocol,
        TokenTimeseriesGroupBy.manager,
    ];

    // Main chart (market cap / total value)
    const mainChart = getAllTokensStackedChart({
        key: `${prefixKey}-market-caps`,
        label: "Total Value",
        description: "",
        filters,
        defaultGroupBy,
        enableGroupByControls,
        measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
        ignoreDownloadsFeatureFlag: true,
        groupByOptions,
        groupLimit: 50,
        groupByForceUpdate: false,
        stacked: {
            enabled: false,
            active: false,
            forceUpdate: true,
        },
    });

    // Transfer Volume
    const transferVolumeChart = getTransferVolumeChart({
        key: `${prefixKey}-transfer-volume`,
        filters,
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        enableGroupByControls,
        groupByOptions,
        groupByForceUpdate: false,
        groupLimit: 50,
        loginRequired: true,
    });

    // Active Addresses
    const activeAddressesChart = getActiveAddressesChart({
        key: `${prefixKey}-active-addresses`,
        filters,
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        enableGroupByControls,
        groupByOptions,
        groupByForceUpdate: false,
        groupLimit: 50,
        loginRequired: true,
    });

    // Mint/Burn Volume
    const mintBurnChart = getMintBurnVolumeChart({
        key: `${prefixKey}-mint-burn-volume`,
        description: "",
        filters,
        loginRequired: true,
    });

    // Holders chart
    const holdersChart = getHoldersChart({
        key: `${prefixKey}-holders`,
        filters,
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        enableGroupByControls,
        groupByOptions,
        groupByForceUpdate: false,
        groupLimit: 50,
        loginRequired: true,
    });

    const modes = [
        mainChart,
        holdersChart,
        activeAddressesChart,
        transferVolumeChart,
        mintBurnChart,
    ];

    return modes;
}
